import React, { useState, useEffect, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import Modal from './Modal';
import Button from './Button';
import { Employee, DisplayGroup } from '../../shared/types.js';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (groupIds: string[]) => void;
    employee: Employee;
}

const AssignEmployeeToGroupsModal: React.FC<Props> = ({ isOpen, onClose, onSave, employee }) => {
    const { displayGroups, updateDisplayGroup } = useTimeClock();
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

    useEffect(() => {
        if (isOpen) {
            const currentGroupIds = displayGroups
                .filter(g => g.employeeIds.includes(employee.id))
                .map(g => g.id);
            setSelectedGroupIds(currentGroupIds);
        }
    }, [isOpen, employee.id]);
    const handleToggleGroup = (id: string) => {
        setSelectedGroupIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
    };
    const handleSave = () => {
        displayGroups.forEach(group => {
            const isCurrentlyMember = group.employeeIds.includes(employee.id);
            const shouldBeMember = selectedGroupIds.includes(group.id);
            if (isCurrentlyMember && !shouldBeMember) {
                const newEmployeeIds = group.employeeIds.filter(uid => uid !== employee.id);
                updateDisplayGroup({ ...group, employeeIds: newEmployeeIds });
            } else if (!isCurrentlyMember && shouldBeMember) {
                const newEmployeeIds = [...group.employeeIds, employee.id];
                updateDisplayGroup({ ...group, employeeIds: newEmployeeIds });
            }
        });
        onSave(selectedGroupIds);
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Assign Groups for ${`${employee.firstName} ${employee.lastName}`.trim()}`}>
            <div className="text-gray-900 dark:text-white">
                <p className="mb-4 text-gray-500 dark:text-gray-400">Select the display groups this employee should belong to.</p>
                 <div className="max-h-80 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-2 space-y-1 bg-gray-50 dark:bg-gray-900/50">
                     {displayGroups.map(group => (
                        <label key={group.id} className="flex items-center gap-3 p-1.5 rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedGroupIds.includes(group.id)}
                                onChange={() => handleToggleGroup(group.id)}
                                className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary focus:ring-2 focus:ring-offset-0"
                            />
                            <span>{group.name}</span>
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="button" onClick={handleSave}>Save</Button>
                </div>
            </div>
        </Modal>
    );
};

export default AssignEmployeeToGroupsModal;